import { MultiselectAutosuggest } from '@seek/multiselect-autosuggest';
import { useTranslations } from '@vocab/react';
import {
  Box,
  IconGlobe,
  Loader,
  CheckboxStandalone,
  Columns,
  Column,
} from 'braid-design-system';
import { type ComponentProps, type FC, useState } from 'react';

import { useAppConfig } from 'src/constants/AppConfig/AppConfigProvider';

import { FilterFacetBadge, FilterFacetTag } from '../FilterFacet';
import { ResponsiveText } from '../ResponsiveText';

import translations from './.vocab';
import { fuzzyFilter } from './fuzzyFilter';

import * as styles from './FilterNationality.css';

export interface NationalityFacet {
  id: string;
  name: string;
  count: number;
}

interface TrackingChange {
  type: 'Selected' | 'Deselected';
  item: NationalityFacet;
}

interface FilterNationalityProps {
  hideLabel: boolean;
  nationalityFacets: NationalityFacet[];
  selectedNationalityFacets: NationalityFacet[];
  setSelectedNationalityFacets: (props: {
    selectedNationalityFacets: NationalityFacet[];
    changes: TrackingChange[];
  }) => void;
  dropdownBoxProps: ComponentProps<typeof Box>;
  loading: boolean;
}

const createItemCountMap = (facets: NationalityFacet[]) =>
  facets.reduce<Record<string, number>>((map, facet) => {
    map[facet.id] = facet.count;
    return map;
  }, {});

export const FilterNationality: FC<FilterNationalityProps> = ({
  nationalityFacets,
  dropdownBoxProps,
  hideLabel,
  loading,
  selectedNationalityFacets,
  setSelectedNationalityFacets,
}) => {
  const { t } = useTranslations(translations);
  const { language } = useAppConfig();
  const sortAlphabetically = (a: NationalityFacet, b: NationalityFacet) =>
    a.name.localeCompare(b.name, language);

  const [searchText, setSearchText] = useState<string>('');

  const sortedNationalityFacets = nationalityFacets.sort(sortAlphabetically);
  const selectedItemCountMap = createItemCountMap(nationalityFacets);
  const filteredSuggestions =
    searchText.length === 0
      ? sortedNationalityFacets
      : fuzzyFilter({
          items: nationalityFacets,
          searchText,
        });

  const iconGlobe = (
    <ResponsiveText
      size={{
        mobile: 'standard',
        tablet: 'small',
      }}
      fallbackSize="small"
      baseline={false}
      tone={
        selectedNationalityFacets.length > 0 || searchText
          ? 'neutral'
          : 'secondary'
      }
    >
      <IconGlobe />
    </ResponsiveText>
  );

  const id = 'nationality-filter';

  const baseBoxProps = {
    background: 'surface',
    borderRadius: 'standard',
    width: 'full',
  } as const;

  if (loading) {
    return (
      <MultiselectAutosuggest
        id={id}
        label={t('Nationality')}
        size="small"
        placeholder={t('Search applications by nationality')}
        suggestions={[]}
        selectedItems={[]}
        searchText=""
        renderSelectedItems={() => iconGlobe}
        setSearchText={() => {}}
        onItemsSelected={() => {}}
        hideLabel={hideLabel}
        dropdownBoxProps={{ ...baseBoxProps, ...dropdownBoxProps }}
        renderSuggestions={() => (
          <Box width="full" padding="small">
            <Loader size="small" aria-label={t('Loading nationalities')} />
          </Box>
        )}
        onClickSuggestionItem={() => {}}
      />
    );
  }

  const onClickSuggestionItem = (itemClicked: NationalityFacet): void => {
    const isSelected = selectedNationalityFacets.some(
      (item) => itemClicked.id === item.id,
    );
    if (isSelected) {
      setSelectedNationalityFacets({
        changes: [{ type: 'Deselected', item: itemClicked }],
        selectedNationalityFacets: selectedNationalityFacets.filter(
          (selectedItem) => selectedItem.id !== itemClicked.id,
        ),
      });
    } else {
      setSelectedNationalityFacets({
        changes: [{ type: 'Selected', item: itemClicked }],
        selectedNationalityFacets: [...selectedNationalityFacets, itemClicked],
      });

      setSearchText('');
    }
  };
  return (
    <MultiselectAutosuggest
      id={id}
      label={t('Nationality')}
      size="small"
      clearLabel={t('Clear')}
      placeholder={t('Search applications by nationality')}
      hideLabel={hideLabel}
      dropdownBoxProps={{ ...baseBoxProps, ...dropdownBoxProps }}
      suggestions={filteredSuggestions}
      selectedItems={selectedNationalityFacets}
      searchText={searchText}
      setSearchText={(newSearchText) => {
        setSearchText(newSearchText);
      }}
      onItemsSelected={(newSelectedItems) => {
        const changes: TrackingChange[] = [];
        newSelectedItems.forEach((item) => {
          if (
            !selectedNationalityFacets.find(
              (selectedNationality) => selectedNationality.id === item.id,
            )
          ) {
            changes.push({
              item,
              type: 'Selected',
            });
          }
        });
        selectedNationalityFacets.forEach((selectedNationaity) => {
          if (
            !newSelectedItems.find((item) => selectedNationaity.id === item.id)
          ) {
            changes.push({
              item: selectedNationaity,
              type: 'Deselected',
            });
          }
        });
        setSelectedNationalityFacets({
          changes,
          selectedNationalityFacets: newSelectedItems,
        });
        setSearchText('');
      }}
      renderSelectedItems={({ getSelectedItemProps }) => (
        <Box
          component="ul"
          display="flex"
          flexWrap="wrap"
          overflow="hidden"
          alignItems="center"
          gap="xxsmall"
          className={styles.selectedItemsBleedY}
        >
          {iconGlobe}
          {selectedNationalityFacets.map((item, index) => {
            const count = selectedItemCountMap[item.id] ?? 0;
            const nationalityCountry = item.name;
            const { ref, ...selectedItemProps } = getSelectedItemProps({
              selectedItem: item,
              index,
            });
            return (
              <FilterFacetTag
                key={`selected-item-${item.id}`}
                label={nationalityCountry}
                count={count}
                onClear={() => onClickSuggestionItem(item)}
                component="li"
                aria-label={t('nationalityCountry count applications', {
                  count,
                  nationalityCountry,
                })}
                innerRef={ref}
                {...selectedItemProps}
              />
            );
          })}
        </Box>
      )}
      renderSuggestions={({ getItemProps, highlightedIndex }) =>
        filteredSuggestions.length > 0 ? (
          <>
            {filteredSuggestions.map((suggestion, itemIndex) => {
              const isHighlighted = highlightedIndex === itemIndex;
              const isSelected = selectedNationalityFacets.some(
                (item) => suggestion.id === item.id,
              );
              const label = t('nationalityCountry count applications', {
                nationalityCountry: suggestion.name,
                count: suggestion.count,
              });
              return (
                <Box
                  key={`suggestion-item-${suggestion.id}`}
                  component="li"
                  cursor="pointer"
                  className={styles.suggestionItemPaddingY}
                  paddingX={['none', 'small']}
                  background={isHighlighted ? 'formAccentSoftHover' : undefined}
                  aria-label={label}
                  {...getItemProps({
                    item: suggestion,
                    name: label,
                    index: itemIndex,
                  })}
                >
                  <Box aria-hidden>
                    <Columns space="small" alignY="top">
                      <Column>
                        <ResponsiveText
                          size={{
                            mobile: 'standard',
                            tablet: 'small',
                          }}
                          fallbackSize="small"
                          icon={
                            <CheckboxStandalone
                              id={`suggestion-checkbox-${suggestion.id}-nationality`}
                              size="small"
                              aria-label={label}
                              checked={isSelected}
                              onChange={(e) => {
                                e.preventDefault();
                                e.stopPropagation();
                              }}
                            />
                          }
                        >
                          {suggestion.name}
                        </ResponsiveText>
                      </Column>
                      <Column width="content">
                        <FilterFacetBadge
                          isHighlighted={isHighlighted}
                          count={suggestion.count}
                          bleedY
                        />
                      </Column>
                    </Columns>
                  </Box>
                </Box>
              );
            })}
          </>
        ) : (
          <Box
            paddingY="small"
            paddingX={['none', 'small']}
            background="surface"
          >
            <ResponsiveText
              size={{
                mobile: 'standard',
                tablet: 'small',
              }}
              fallbackSize="small"
            >
              {t('No nationalities found')}
            </ResponsiveText>
          </Box>
        )
      }
      onClickSuggestionItem={onClickSuggestionItem}
    />
  );
};
